/**
 * Island quiz: size, configuration, and model stay linked to valid
 * product variants from the selected collection. Configuration is
 * multi-select of appliances; size and model are single-select.
 */
class IybriQuiz extends HTMLElement {
  #bound = false;
  combinations = [];
  applianceOrder = [];
  selection = { size: '', configuration: [], model: '' };
  #showingResults = false;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);

    if (this.querySelector('[data-quiz-combinations]')) {
      this.#init();
    } else {
      queueMicrotask(() => this.#init());
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('submit', this.#onSubmit);
    this.#bound = false;
  }

  get requiresSize() {
    return this.combinations.some((row) => Boolean(row.size));
  }

  get requiresModel() {
    return this.combinations.some((row) => Boolean(row.model));
  }

  #init() {
    const payload = this.#readPayload();
    this.applianceOrder = payload.applianceOrder;
    this.combinations = payload.combinations;
    this.update();
  }

  #readPayload() {
    const empty = { applianceOrder: [], combinations: [] };
    const node = this.querySelector('[data-quiz-combinations]');
    if (!node) return empty;

    try {
      const parsed = JSON.parse(node.textContent || '{}');
      const rows = Array.isArray(parsed) ? parsed : parsed.combinations;
      const order = Array.isArray(parsed) ? [] : parsed.applianceOrder;
      const combinations = (Array.isArray(rows) ? rows : []).filter(
        (row) => this.#isAvailable(row.configuration) && this.#isAvailable(row.product)
      );
      const applianceOrder = Array.isArray(order) && order.length
        ? order.filter(Boolean)
        : this.#appliancesFromRows(combinations);

      return { applianceOrder, combinations };
    } catch (error) {
      console.warn('IYBRI quiz: could not parse combinations', error);
      return empty;
    }
  }

  /** @param {{ configuration?: string }[]} rows */
  #appliancesFromRows(rows) {
    const seen = [];
    rows.forEach((row) => {
      String(row.configuration || '')
        .split('+')
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((name) => {
          if (!seen.includes(name)) seen.push(name);
        });
    });
    return seen;
  }

  /** @param {string | undefined} value */
  #isAvailable(value) {
    if (!value) return false;
    return !/^n\/a/i.test(value.trim());
  }

  /**
   * Maps selected appliances to the configuration string stored on variants.
   * @param {string[]} [appliances]
   */
  configKey(appliances = this.selection.configuration) {
    const selected = new Set(appliances);
    const extras = appliances
      .filter((name) => !this.applianceOrder.includes(name))
      .slice()
      .sort();
    return this.applianceOrder.filter((name) => selected.has(name)).concat(extras).join(' + ');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const reset = target.closest('[data-quiz-reset]');
    if (reset && this.contains(reset)) {
      this.#resetQuiz();
      return;
    }

    if (this.#showingResults) return;

    const option = target.closest('[data-quiz-option]');
    if (!option || !this.contains(option) || option.hasAttribute('disabled')) return;

    const dimension = option.getAttribute('data-dimension');
    const value = option.getAttribute('data-value') || '';
    if (dimension === 'configuration') {
      const current = this.selection.configuration;
      this.selection.configuration = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      this.update();
      return;
    }

    if (dimension !== 'size' && dimension !== 'model') return;

    this.selection[dimension] = this.selection[dimension] === value ? '' : value;
    this.update();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    event.preventDefault();
    const matched = this.#uniqueMatch();
    if (!matched) return;

    this.#showingResults = true;
    this.update();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  #resetQuiz() {
    this.selection = { size: '', configuration: [], model: '' };
    this.#showingResults = false;
    this.update();
  }

  /**
   * Config: adding this appliance must produce a key that exists with the
   * current size/model. Selected appliances stay clickable so they can be
   * removed. Size/model: match rows for the current configKey (or any
   * config if none selected). Selected size/model stay clickable.
   * @param {'size' | 'configuration' | 'model'} dimension
   * @param {string} value
   */
  #isEnabled(dimension, value) {
    if (dimension === 'configuration') {
      if (this.selection.configuration.includes(value)) return true;

      const nextKey = this.configKey([...this.selection.configuration, value]);
      if (!nextKey) return false;

      return this.combinations.some((row) => this.#rowMatches(row, {
        size: this.selection.size,
        model: this.selection.model,
        configuration: nextKey,
      }));
    }

    if (this.selection[dimension] === value) return true;

    const key = this.configKey();
    return this.combinations.some((row) => {
      if (row[dimension] !== value) return false;
      return this.#rowMatches(row, {
        size: dimension === 'size' ? '' : this.selection.size,
        model: dimension === 'model' ? '' : this.selection.model,
        configuration: key,
      });
    });
  }

  /**
   * @param {{ size: string, configuration: string, model: string, handle?: string }} row
   * @param {{ size?: string, model?: string, configuration?: string }} filters
   */
  #rowMatches(row, filters) {
    if (filters.configuration && row.configuration !== filters.configuration) return false;
    if (filters.size && row.size !== filters.size) return false;
    if (filters.model && row.model !== filters.model) return false;
    return true;
  }

  /**
   * A unique product requires a valid config set, plus size/model when
   * those options exist on the products.
   */
  #uniqueMatch() {
    const { size, model } = this.selection;
    const key = this.configKey();
    if (!key) return null;
    if (this.requiresSize && !size) return null;
    if (this.requiresModel && !model) return null;

    const matches = this.combinations.filter((row) =>
      this.#rowMatches(row, { size, model, configuration: key })
    );

    if (matches.length === 0) return null;
    const handle = matches[0].handle || matches[0].product;
    if (matches.every((row) => (row.handle || row.product) === handle)) return matches[0];
    return null;
  }

  /** @param {string} dimension @param {string} value */
  #optionButton(dimension, value) {
    const buttons = this.querySelectorAll(`[data-quiz-option][data-dimension="${dimension}"]`);
    return Array.from(buttons).find((button) => button.getAttribute('data-value') === value) || null;
  }

  /** @param {string} size */
  #sizeChipLabel(size) {
    const button = this.#optionButton('size', size);
    return button?.querySelector('.iybri-quiz__option-label')?.textContent?.trim() || size;
  }

  /** @param {HTMLElement} mount @param {string} dimension @param {string} value @param {string} label */
  #appendChip(mount, dimension, value, label) {
    const chip = document.createElement('span');
    chip.className = 'iybri-quiz__chip';

    const source = this.#optionButton(dimension, value);
    const iconSource = source?.querySelector('.iybri-quiz__option-icon, .iybri-quiz__option-glyph');
    if (iconSource) {
      const icon = document.createElement('span');
      icon.className = 'iybri-quiz__chip-icon';
      icon.append(iconSource.cloneNode(true));
      chip.append(icon);
    }

    const text = document.createElement('span');
    text.className = 'iybri-quiz__chip-label';
    text.textContent = label;
    chip.append(text);
    mount.append(chip);
  }

  /** @param {{ size: string, configuration: string, model: string, product: string, url?: string, image?: string | null, description?: string }} matched */
  #renderResults(matched) {
    const product = this.querySelector('[data-quiz-product]');
    const chips = this.querySelector('[data-quiz-chips]');
    const link = this.querySelector('[data-quiz-product-link]');
    const illustration = this.querySelector('[data-quiz-illustration]');
    const description = this.querySelector('[data-quiz-description]');

    if (product) product.textContent = matched.product;

    if (link instanceof HTMLAnchorElement) {
      link.dataset.product = matched.product;
      if (matched.url) link.href = matched.url;
    }

    if (illustration instanceof HTMLElement) {
      const existing = illustration.querySelector('[data-quiz-product-image]');
      const imageUrl = typeof matched.image === 'string' ? matched.image.trim() : '';
      if (imageUrl) {
        const img = existing instanceof HTMLImageElement ? existing : document.createElement('img');
        img.setAttribute('data-quiz-product-image', '');
        img.alt = matched.product || '';
        img.src = imageUrl;
        if (!existing) illustration.prepend(img);
        illustration.classList.add('has-image');
      } else if (existing) {
        existing.remove();
        illustration.classList.remove('has-image');
      } else {
        illustration.classList.remove('has-image');
      }
    }

    if (description instanceof HTMLElement) {
      const text = typeof matched.description === 'string' ? matched.description.trim() : '';
      const showImage = illustration instanceof HTMLElement && illustration.classList.contains('has-image');
      if (text && showImage) {
        description.innerHTML = text;
        description.hidden = false;
      } else {
        description.replaceChildren();
        description.hidden = true;
      }
    }

    if (chips) {
      chips.replaceChildren();
      if (matched.size) {
        this.#appendChip(chips, 'size', matched.size, this.#sizeChipLabel(matched.size));
      }
      this.applianceOrder.forEach((appliance) => {
        if (this.selection.configuration.includes(appliance)) {
          this.#appendChip(chips, 'configuration', appliance, appliance);
        }
      });
      if (matched.model) {
        this.#appendChip(chips, 'model', matched.model, matched.model);
      }
    }
  }

  update() {
    this.querySelectorAll('[data-quiz-option]').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;

      const dimension = option.getAttribute('data-dimension');
      const value = option.getAttribute('data-value') || '';
      if (dimension !== 'size' && dimension !== 'configuration' && dimension !== 'model') return;

      const selected =
        dimension === 'configuration'
          ? this.selection.configuration.includes(value)
          : this.selection[dimension] === value;
      const enabled = this.#isEnabled(dimension, value);

      option.classList.toggle('is-selected', selected);
      option.classList.toggle('is-disabled', !enabled);
      option.setAttribute('aria-pressed', selected ? 'true' : 'false');

      if (enabled) {
        option.removeAttribute('disabled');
        option.removeAttribute('aria-disabled');
      } else {
        option.setAttribute('disabled', '');
        option.setAttribute('aria-disabled', 'true');
      }
    });

    const matched = this.#uniqueMatch();
    const submit = this.querySelector('[data-quiz-submit]');
    if (submit instanceof HTMLButtonElement) {
      submit.disabled = !matched;
    }

    const form = this.querySelector('[data-quiz-form]');
    const results = this.querySelector('[data-quiz-results]');
    if (form instanceof HTMLElement) form.hidden = this.#showingResults;
    if (results instanceof HTMLElement) results.hidden = !this.#showingResults;
    this.classList.toggle('is-results', this.#showingResults);

    if (this.#showingResults && matched) {
      this.#renderResults(matched);
    }
  }
}

if (!customElements.get('iybri-quiz')) {
  customElements.define('iybri-quiz', IybriQuiz);
}
